import { useMutation, useQueryClient } from "react-query";
import { t } from "@/i18n-js/instance";
import type { UpdateResponseData } from "@circle-react/api/marketingEmailTemplateApi";
import { marketingEmailTemplatesApi } from "@circle-react/api/marketingEmailTemplateApi";
import { getBroadcastQueryKey } from "@circle-react/components/SettingsApp/Emails/hooks/useGetBroadcast";
import type { ApiError } from "@circle-react/config/CustomErrors";
import type { MarketingEmailTemplateFormFields } from "@circle-react/types/MarketingEmailTemplates";
import { useToast } from "@circle-react-shared/uikit/ToastV2";
import { templateLocaleNamespace } from "../../UpsertTemplateModal/UpdateTemplateContent";
import { getTemplateDetailQueryKey } from "./useTemplateDetail";
import { MARKETING_EMAIL_TEMPLATE_LIST_QUERY_KEY_PREFIX } from "./useTemplatesInfiniteQuery";

interface UpdateMutationArgs {
  body: MarketingEmailTemplateFormFields;
  broadcastId?: number;
  id: number;
}

export const useUpdateTemplate = () => {
  const queryClient = useQueryClient();
  const toastr = useToast();

  return useMutation<UpdateResponseData, ApiError, UpdateMutationArgs>({
    mutationFn: ({ body, id, broadcastId }) => {
      const payload = {
        marketing_email_template: {
          name: body.name,
          content: body.content,
          template_type: body.template_type,
          email_hub_theme_id: body.email_theme?.id,
          visual_email_builder_content: body.visual_email_builder_content,
          visual_email_builder_content_html:
            body.visual_email_builder_content_html,
        },
        broadcast_id: broadcastId,
      };

      return marketingEmailTemplatesApi.update({
        id,
        body: payload,
      });
    },
    onSuccess: (_, { broadcastId, id }) => {
      toastr.success(t([templateLocaleNamespace, "template_updated"]));
      void queryClient.invalidateQueries(getTemplateDetailQueryKey(id));
      void queryClient.invalidateQueries({
        queryKey: MARKETING_EMAIL_TEMPLATE_LIST_QUERY_KEY_PREFIX,
      });
      if (broadcastId) {
        void queryClient.invalidateQueries(getBroadcastQueryKey(broadcastId));
      }
    },
    onError: error => {
      if (error.message) {
        return toastr.error(error.message);
      }

      toastr.error(t("unexpected_error"));
    },
  });
};
